import React, { useMemo, useState } from 'react';
import { Download, Mail, Settings2 } from 'lucide-react';
import { useCutierStore } from '../../store/useCutierStore';
import { enqueueCutierInvoiceMail } from '../../services/cutierFirebaseService';
import {
  buildCutierInvoiceHybridPdf,
  buildCutierInvoicePdf,
  downloadCutierInvoicePdf,
} from '../../utils/cutier/cutierInvoicePdfGenerator';
import {
  assessCutierInvoiceProfile,
  formatSenderLine,
} from '../../utils/cutier/cutierInvoiceProfileUtils';
import { sumLeadUnits } from '../../utils/cutier/cutierLeadUtils';
import CutierInvoiceSettingsModal from './CutierInvoiceSettingsModal';

const formatEuro = (value: number) => `${value.toFixed(2).replace('.', ',')} €`;

const CutierBillingCockpit: React.FC = () => {
  const selectedMonth = useCutierStore((s) => s.selectedMonth);
  const leads = useCutierStore((s) => s.leads);
  const rateGross = useCutierStore((s) => s.rateGross);
  const invoiceProfile = useCutierStore((s) => s.invoiceProfile);
  const invoiceProfileLoaded = useCutierStore((s) => s.invoiceProfileLoaded);
  const setInvoiceProfile = useCutierStore((s) => s.setInvoiceProfile);

  const customerCount = leads.length;
  const unitCount = useMemo(() => sumLeadUnits(leads), [leads]);
  const grossTotal = unitCount * rateGross;
  const netTotal = grossTotal / 1.19;
  const vatTotal = grossTotal - netTotal;

  const profileStatus = useMemo(
    () => assessCutierInvoiceProfile(invoiceProfile),
    [invoiceProfile]
  );
  const invoiceReady = profileStatus.complete;
  const canInvoice = unitCount > 0 && invoiceReady;

  const [busy, setBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  );

  const invoiceParams = useMemo(
    () => ({
      monthKey: selectedMonth,
      leads,
      rateGross,
      profile: invoiceProfile,
    }),
    [invoiceProfile, leads, rateGross, selectedMonth]
  );

  const handleDownloadPdf = async () => {
    if (!canInvoice) return;
    try {
      await downloadCutierInvoicePdf(invoiceParams, profileStatus.eInvoiceReady);
    } catch {
      setFeedback({ type: 'error', text: 'PDF konnte nicht erstellt werden.' });
    }
  };

  const handleSendInvoice = async () => {
    if (!canInvoice) return;
    setBusy(true);
    setFeedback(null);
    try {
      const pdfResult = profileStatus.eInvoiceReady
        ? await buildCutierInvoiceHybridPdf(invoiceParams)
        : await buildCutierInvoicePdf(invoiceParams);

      const buyerName = invoiceProfile.buyerContactPerson || invoiceProfile.buyerCompanyName;
      const senderLine = formatSenderLine(invoiceProfile);
      const docLabel = profileStatus.eInvoiceReady ? 'E-Rechnung (ZUGFeRD)' : 'PDF-Rechnung';

      const invoiceHtml = `
          <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; color: #1e293b; line-height: 1.6;">
            <p style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Abrechnung Seminar ${selectedMonth}</p>
            <p>Hallo ${buyerName},</p>
            <p>für den Leistungszeitraum <strong>${selectedMonth}</strong> liegen <strong>${unitCount}</strong> Seminar-Bestellungen vor (${customerCount} Kunden).</p>
            <p>
              Netto: ${formatEuro(netTotal)}<br />
              zzgl. 19% MwSt.: ${formatEuro(vatTotal)}<br />
              <strong>Abrechnungssumme brutto: ${formatEuro(grossTotal)}</strong>
            </p>
            <p>Im Anhang finden Sie die ${docLabel}. Bitte prüfen und den Betrag innerhalb von ${invoiceProfile.defaultPaymentTermsDays || 14} Tagen überweisen.</p>
            <p style="font-size: 12px; color: #94a3b8; margin-top: 30px;">${senderLine}</p>
          </div>
        `;
      const invoiceText = `Hallo ${buyerName},\n\nfür den Leistungszeitraum ${selectedMonth} liegen ${unitCount} Seminar-Bestellungen vor (${customerCount} Kunden).\nAbrechnungssumme brutto: ${formatEuro(grossTotal)} (netto ${formatEuro(netTotal)}, zzgl. 19% MwSt. ${formatEuro(vatTotal)}).\n\nIm Anhang: ${docLabel}.\n\nHerzliche Grüße\n${invoiceProfile.displayName}`;

      const result = await enqueueCutierInvoiceMail({
        to: invoiceProfile.buyerEmail,
        name: invoiceProfile.buyerCompanyName,
        subject: `Abrechnung Seminar ${selectedMonth} — ${invoiceProfile.displayName}`,
        mailTitle: `Abrechnung — ${invoiceProfile.displayName}`,
        html: invoiceHtml,
        text: invoiceText,
        replyTo: invoiceProfile.email,
        invoiceNr: pdfResult.invoiceNumber,
        fileName: pdfResult.fileName,
        monthKey: selectedMonth,
        pdfBytes: pdfResult.pdfBytes,
        pdfBase64: pdfResult.base64PDF,
      });
      setFeedback({
        type: result.ok ? 'success' : 'error',
        text: result.ok
          ? `Rechnung an ${invoiceProfile.buyerEmail} gesendet${profileStatus.eInvoiceReady ? ' (E-Rechnung)' : ''}${result.usedAttachment ? '' : ' — Download-Link in der Mail'}.`
          : `Versand fehlgeschlagen${result.error ? `: ${result.error}` : '.'}`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Versand fehlgeschlagen.';
      setFeedback({ type: 'error', text: message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-black uppercase tracking-wider text-pe-text">
          Abrechnungs-Cockpit
        </h3>
        <span className="rounded-full border border-[#E6C299]/40 bg-[#E6C299]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-pe-accent-text">
          {selectedMonth}
        </span>
      </div>

      <div
        className={
          invoiceReady
            ? 'rounded-xl border border-emerald-800/50 bg-emerald-950/30 px-3 py-2.5 text-xs text-emerald-300'
            : 'rounded-xl border border-amber-800/50 bg-amber-950/30 px-3 py-2.5 text-xs text-amber-200'
        }
      >
        {!invoiceProfileLoaded ? (
          <p>Rechnungsdaten werden geladen…</p>
        ) : invoiceReady ? (
          <p>
            <strong>E-Rechnung bereit.</strong> PDF-Download und Versand an{' '}
            {invoiceProfile.buyerEmail} sind freigeschaltet.
          </p>
        ) : (
          <p>
            <strong>Rechnung gesperrt.</strong> Bitte zuerst IBAN, Bank und Steuerdaten hinterlegen
            {profileStatus.missing.length > 0
              ? ` (fehlend: ${profileStatus.missing.join(', ')})`
              : ''}
            .
          </p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl border border-pe-border-strong bg-pe-sunken p-3">
          <p className="text-[9px] font-bold uppercase tracking-widest text-pe-muted">Bestellungen</p>
          <p className="mt-1 text-2xl font-bold text-pe-accent-text">{unitCount}</p>
          <p className="mt-0.5 text-[9px] text-pe-muted">{customerCount} Kunden</p>
        </div>
        <div className="rounded-xl border border-pe-border-strong bg-pe-sunken p-3">
          <p className="text-[9px] font-bold uppercase tracking-widest text-pe-muted">Netto (19%)</p>
          <p className="mt-1 text-sm font-bold text-pe-text">{formatEuro(netTotal)}</p>
        </div>
        <div className="rounded-xl border border-pe-border-strong bg-pe-sunken p-3">
          <p className="text-[9px] font-bold uppercase tracking-widest text-pe-muted">Brutto</p>
          <p className="mt-1 text-2xl font-bold text-emerald-400">{formatEuro(grossTotal)}</p>
        </div>
      </div>

      {feedback ? (
        <div
          className={
            feedback.type === 'success'
              ? 'rounded-lg border border-emerald-800 bg-emerald-950/40 p-3 text-sm text-emerald-400'
              : 'rounded-lg border border-red-800 bg-red-950/40 p-3 text-sm text-red-300'
          }
        >
          {feedback.text}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => void handleDownloadPdf()}
          disabled={!canInvoice || busy}
          title={
            !invoiceReady
              ? 'Zuerst Rechnungsdaten vervollständigen'
              : unitCount === 0
                ? 'Keine Bestellungen'
                : undefined
          }
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-pe-border-strong bg-pe-input px-4 py-3 text-[10px] font-black uppercase tracking-widest text-pe-text hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Download className="h-4 w-4" />
          {profileStatus.eInvoiceReady ? 'E-Rechnung herunterladen' : 'PDF-Rechnung herunterladen'}
        </button>
        <button
          type="button"
          onClick={() => void handleSendInvoice()}
          disabled={!canInvoice || busy}
          title={
            !invoiceReady
              ? 'Zuerst Rechnungsdaten vervollständigen'
              : unitCount === 0
                ? 'Keine Bestellungen'
                : undefined
          }
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#E6C299] px-4 py-3 text-[10px] font-black uppercase tracking-widest text-[#030303] hover:bg-[#d4b086] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Mail className="h-4 w-4" />
          {busy ? 'Sende…' : `Rechnung an ${invoiceProfile.buyerCompanyName || 'CUTIER'} senden`}
        </button>
      </div>

      <button
        type="button"
        onClick={() => setSettingsOpen(true)}
        className={`inline-flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-4 text-xs font-black uppercase tracking-widest transition-colors ${
          invoiceReady
            ? 'border-pe-border-strong bg-pe-input text-pe-muted hover:text-pe-text'
            : 'border-[#E6C299] bg-[#E6C299]/15 text-[#E6C299] shadow-lg shadow-[#E6C299]/10 hover:bg-[#E6C299]/25'
        }`}
      >
        <Settings2 className="h-5 w-5" />
        {invoiceReady ? 'Rechnungsdaten bearbeiten' : 'Rechnungsdaten eingeben'}
      </button>

      <CutierInvoiceSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        profile={invoiceProfile}
        onSaved={setInvoiceProfile}
      />
    </div>
  );
};

export default CutierBillingCockpit;
